import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useCashoutAccounts } from '../../../hooks/use-cashout-accounts'
import formStyles from '../form.module.css'
import styles from './account-selector.module.css'

// Assets
import iconoBanco from '../../../assets/icono-banco.png'
import iconoTarjeta from '../../../assets/icono-tarjeta.png'

const OTHER_ACCOUNT_ID = 'OTHER'

const normalizeText = (value) => String(value ?? '').trim()

const getAccountData = (account) => {
  const rawName = normalizeText(account?.name)
  const holderName = normalizeText(account?.holder_name)
  const holderLastName = normalizeText(account?.holder_last_name)
  const holderFullName = `${holderName} ${holderLastName}`.trim()
  const accountType = normalizeText(account?.type).toLowerCase()

  const primaryLabel = rawName || `Cuenta ${account?.id ?? ''}`.trim()
  const secondaryLabel = holderFullName || 'Titular no disponible'
  const icon = accountType === 'spei' ? iconoBanco : iconoTarjeta

  return {
    icon,
    primaryLabel,
    secondaryLabel,
  }
}

export const AccountSelector = ({
  name = 'cashout_account_id',
  label = 'Cuenta para retiro',
  helpText = '',
  required = false,
  disabled = false,
  autoFocus = false,
  allowNewAccount = false,
}) => {
  const { control } = useFormContext()
  const { data: cashoutAccountsData, isLoading } = useCashoutAccounts()
  const accounts = cashoutAccountsData?.cashoutAccounts ?? []

  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      render={({ field, fieldState }) => {
        const describedBy = [
          fieldState.error ? `${name}-error` : null,
          helpText ? `${name}-help` : null,
        ]
          .filter(Boolean)
          .join(' ')

        if (isLoading) {
          return (
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor={name}>
                {label} {required && <span className={formStyles.required}>*</span>}
              </label>
              <p className={formStyles.helpText}>Cargando cuentas...</p>
            </div>
          )
        }

        if (!accounts.length) {
          return null
        }

        return (
          <div className={formStyles.field}>
            <label className={formStyles.label} htmlFor={name}>
              {label} {required && <span className={formStyles.required}>*</span>}
            </label>

            <div
              className={styles.list}
              role="radiogroup"
              aria-invalid={fieldState.error ? 'true' : 'false'}
              aria-describedby={describedBy || undefined}
            >
              {accounts.map((account, index) => {
                const optionValue = String(account?.id ?? '')
                const isChecked = String(field.value ?? '') === optionValue
                const accountData = getAccountData(account)

                return (
                  <button
                    key={optionValue || `account-${index}`}
                    type="button"
                    className={`${styles.option} ${isChecked ? styles.optionSelected : ''}`}
                    onClick={() => {
                      if (disabled) return
                      field.onChange(account?.id ?? '')
                    }}
                    onBlur={field.onBlur}
                    disabled={disabled}
                    role="radio"
                    aria-checked={isChecked}
                    aria-label={accountData.primaryLabel}
                    autoFocus={autoFocus && index === 0}
                  >
                    <span className={styles.leftContent}>
                      <span className={styles.iconWrap}>
                        <img src={accountData.icon} alt="" height={24} aria-hidden />
                      </span>
                      <span className={styles.textWrap}>
                        <span className={styles.primaryText}>{accountData.primaryLabel}</span>
                        <span className={styles.secondaryText}>{accountData.secondaryLabel}</span>
                      </span>
                    </span>

                    <span className={styles.radioIndicator} aria-hidden>
                      <span className={styles.radioDot} />
                    </span>

                    <input
                      id={`${name}-${optionValue}`}
                      name={field.name}
                      className={styles.hiddenInput}
                      type="radio"
                      value={optionValue}
                      checked={isChecked}
                      onChange={() => field.onChange(account?.id ?? '')}
                      onBlur={field.onBlur}
                      disabled={disabled}
                      tabIndex={-1}
                      aria-hidden
                    />
                  </button>
                )
              })}

              {allowNewAccount ? (
                <button
                  type="button"
                  className={`${styles.option} ${String(field.value ?? '') === OTHER_ACCOUNT_ID ? styles.optionSelected : ''}`}
                  onClick={() => {
                    if (disabled) return
                    field.onChange(OTHER_ACCOUNT_ID)
                  }}
                  onBlur={field.onBlur}
                  disabled={disabled}
                  role="radio"
                  aria-checked={String(field.value ?? '') === OTHER_ACCOUNT_ID}
                  aria-label="Seleccionar otra cuenta"
                >
                  <span className={styles.leftContent}>
                    {/* <span className={styles.iconWrap}>
                      <img src={iconoBanco} alt="" height={24} aria-hidden />
                    </span> */}
                    <span className={styles.textWrap}>
                      <span className={styles.primaryText}>Agregar otra cuenta</span>
                    </span>
                  </span>

                  <span className={styles.radioIndicator} aria-hidden>
                    <span className={styles.radioDot} />
                  </span>

                  <input
                    id={`${name}-${OTHER_ACCOUNT_ID}`}
                    name={field.name}
                    className={styles.hiddenInput}
                    type="radio"
                    value={OTHER_ACCOUNT_ID}
                    checked={String(field.value ?? '') === OTHER_ACCOUNT_ID}
                    onChange={() => field.onChange(OTHER_ACCOUNT_ID)}
                    onBlur={field.onBlur}
                    disabled={disabled}
                    tabIndex={-1}
                    aria-hidden
                  />
                </button>
              ) : null}
            </div>

            {fieldState.error ? (
              <p id={`${name}-error`} className={formStyles.error} role="alert">
                {fieldState.error.message}
              </p>
            ) : null}

            {helpText ? (
              <p id={`${name}-help`} className={formStyles.helpText}>
                {helpText}
              </p>
            ) : null}
          </div>
        )
      }}
    />
  )
}